/**
 * Transforms an array of booking data into a format suitable for rendering the multiple-booking display
 * @param {Array} bookingsArray - an array of booking objects
 * @param {Function} unitDataGetter - a state getter function for retrieving unit data by unit id
 * @param {Function} siteNameGetter - a state getter function for resolving a unit's site name
 * @returns
 */
export const createBookingDisplayArray = (bookingArray, unitDataGetter, siteNameGetter) =>
    bookingArray.map((booking) => {
        const unitData = unitDataGetter(booking.unit) ?? {};
        const subUnitData = booking.sub_unit
            ? unitDataGetter(booking.sub_unit)
            : null;

        const hasDiscount = booking.costs.discountUnformatted > 0;

        return {
            unitName               : unitData.name ?? '',
            unitThumbnail          : unitData.thumbnail ?? '',
            subUnitName            : subUnitData?.name ?? '',
            unitAddress            : unitData.address ?? '',
            siteName               : siteNameGetter?.(unitData) ?? '',
            arrivalDateLongFormat  : booking.arrival_date ?? '',
            departureDateLongFormat: booking.departure_date ?? '',
            lengthOfStay           : booking.period ?? '',
            totalCostFloat         : booking.costs.totalUnformatted ?? 0,
            totalCostString        : booking.costs.total ?? '',
            hasDiscount            : hasDiscount,
            totalNormalString      : booking.costs.totalNormal ?? ''
        }
    });


export const transformCostData = (data) => ({
    base                      : data.base,
    baseUnformatted           : parseFloat(data.base_unformatted),
    baseWithPartyUnformatted  : parseFloat(data.base_with_party_unformatted),
    baseWithParty             : formatPriceString(data.base_with_party),
    baseDiscount              : data.base_discount,
    baseDiscountUnformatted   : parseFloat(data.base_discount_unformatted),  

    totalNetFormatted       : formatPriceString(data.total_net ?? '0'),
    totalVatFormatted       : formatPriceString(data.total_vat ?? '0'),
    total                   : formatPriceString(data.total ?? '0'),
    totalUnformatted        : parseFloat(data.total_unformatted ?? 0),
    totalToPay              : formatPriceString(data.total_to_pay ?? '0'),
    totalNormal             : formatPriceString(data.total_normal ?? '0'),
    totalNormalUnformatted  : parseFloat(data.total_normal_unformatted ?? 0),
    totalToPayUnformatted   : parseFloat(data.total_to_pay_unformatted ?? 0),

    agent                   : formatPriceString(data.agent ?? '0'),
    agentUnformatted        : parseFloat(data.agent_unformatted ?? 0),
    discount                : formatPriceString(data.discount ?? '0'),
    discountUnformatted     : parseFloat(data.discount_unformatted ?? 0),
    discountPercentage      : parseInt(data.discount_percentage, 10),
    isPercentageDiscount    : data.is_percentage,
    discountCode            : data.discount_code,

    fullOnly                : data.full_only,

    partyMembers            : data.party_members ?? '0',
    partyMembersUnformatted : parseFloat(data.party_members_unformatted ?? 0),
    partyMembersBreakdown   : data.party_members_breakdown || [],
    partySize               : parseIntb10(data.party_size ?? 0),
    chargeablePartySize     : parseIntb10(data.chargeable_party_size ?? 0),

    addons                  : formatPriceString(data.addons ?? '0'),
    addonsUnformatted       : parseFloat(data.addons_unformatted ?? 0),
    addonsBreakdown         : data.addons_breakdown || [],

    deposit                 : formatPriceString(data.deposit ?? '0'),
    depositUnformatted      : parseFloat(data.deposit_unformatted ?? 0),
    balance                 : formatPriceString(data.balance2 ?? '0'),
    balanceFormatted        : formatPriceString(data.balance2 ?? '0'),
    balancedUnformatted     : parseFloat(data.balance_unformatted ?? 0),
    balanceDueDate          : data.balance_due_date || null,
    balanceOnArrival        : data.balance_on_arrival === true || parseIntb10(data.balance_on_arrival) === 1,
    balanceType             : parseIntb10(data.balance_type ?? 1),

    freeOfCharge            : parseIntb10(data.total_to_pay_unformatted ?? 0) === 0,

    // Totals for Multiple bookings
    allBookingsTotalUnformatted      : parseFloat(data.all_bookings_total_unformatted ?? 0),
    allBookingsTotal                 : formatPriceString(data.all_bookings_total ?? '0'),
    allBookingsTotalToPayUnformatted : parseFloat(data.all_bookings_total_to_pay_unformatted ?? 0),
    allBookingsTotalToPay            : formatPriceString(data.all_bookings_total_to_pay ?? '0'),

    rawBookingInfo : data.booking_info || {}
});
    
/**
 * Derives actual payment type using the PHP costs object. A deposit that covers
 * the full booking total is not a meaningful alternative to a full payment.
 * @param {boolean} isFullOnly - used with the 'full_only' value in the costs object
 * @param {string} depositString - used with the 'deposit_unformatted' value in the costs object
 * @param {string} totalString - used with the 'total_unformatted' value in the costs object
 * @returns {boolean}
 */
export const isUsingFullPaymentOnly = (isFullOnly, depositString, totalString) => {
    const deposit = parseFloat(depositString);
    const total = parseFloat(totalString);

    return isFullOnly || deposit === 0 || (Number.isFinite(deposit) && Number.isFinite(total) && deposit >= total);
};

export const sumTotalPartyMembers = (partyMemberObject) => Object
                                            .values(partyMemberObject)
                                            .reduce((sum, value) => value.calc_include == 1 ? sum + parseInt(value.qty, 10) : sum, 0);
